import {
  buildDraft,
  centsFromInput,
  clampName,
  locationSteps,
  normaliseMarkup,
  parseDay,
  priceWithMarkup,
  shelveOf,
  type ImportSheet,
  type LocationCount,
} from "@iltocat/shared";
import { ApiError, api } from "./api.js";

/**
 * Writes an import: the names a row uses become ids, then each row is created
 * through the same routes capture uses, and in a stockroom put on the shelf
 * through the same route the item screen's "Put on the shelf" uses. Nothing
 * here knows a rule of its own, so an import is refused exactly where typing
 * the item in would be.
 *
 * A row's outcome is recorded as it lands, so a failure part-way leaves a
 * report rather than a mystery, and running again only sends what is not done:
 * a row that was created and then refused the shelf is not created twice.
 */

export type RowOutcome =
  | { state: "waiting" }
  | { state: "working" }
  | { state: "done"; itemId: string }
  | { state: "failed"; message: string; itemId?: string; shelved?: boolean };

export interface ImportPlan {
  sheet: ImportSheet;
  /** Indexes of the rows to write. */
  rows: number[];
  /** Whether to shelve each as stock (a stockroom only). */
  shelve: boolean;
  /** `YYYY-MM-DD`, the day the stock came in. */
  receivedOn: string;
  /**
   * What a row's client is billed for the deployment, when the row does not
   * say: its cost plus this markup. Only read when a row names a client.
   */
  markupPercent: number;
  /** Whether a deployment made by the import is billable. Off: these went out before utucat. */
  billable: boolean;
}

const CONCURRENCY = 4;

const reason = (problem: unknown): string =>
  problem instanceof ApiError || problem instanceof Error ? problem.message : "Something went wrong.";

const lower = (name: string): string => name.toLowerCase();

/** Finds or makes every category and location the rows name, sequentially: a child needs its parent's id. */
async function resolveNames(plan: ImportPlan): Promise<{
  category: (name: string) => string | null;
  location: (path: string) => string | null;
}> {
  const categories = new Map<string, string>();
  for (const category of (await api.categories.list()).categories) {
    categories.set(lower(category.name), category.id);
  }
  const wanted = new Set<string>();
  for (const at of plan.rows) {
    const name = clampName(plan.sheet.rows[at]?.category);
    if (name) wanted.add(name);
  }
  for (const name of wanted) {
    if (categories.has(lower(name))) continue;
    const { categories: after } = await api.categories.create(name);
    const made = after.find((category) => lower(category.name) === lower(name));
    if (made) categories.set(lower(name), made.id);
  }

  let locations: LocationCount[] = (await api.locations.list()).locations;
  const step = new Map<string, string>();
  const index = () => {
    step.clear();
    for (const one of locations) step.set(`${one.parentId ?? ""}\u0000${lower(one.name)}`, one.id);
  };
  index();

  const paths = new Map<string, string | null>();
  for (const at of plan.rows) {
    const steps = locationSteps(plan.sheet.rows[at]?.location ?? "");
    const pathKey = steps.map(lower).join("\u0001");
    if (!steps.length || paths.has(pathKey)) continue;

    let parent: string | null = null;
    for (const name of steps) {
      const key = `${parent ?? ""}\u0000${lower(name)}`;
      let id = step.get(key);
      if (!id) {
        locations = (await api.locations.create(name, parent)).locations;
        index();
        id = step.get(key);
      }
      if (!id) break;
      parent = id;
    }
    paths.set(pathKey, parent);
  }

  return {
    category: (name) => categories.get(lower(clampName(name))) ?? null,
    location: (path) => paths.get(locationSteps(path).map(lower).join("\u0001")) ?? null,
  };
}

/** Finds or makes every client the rows name, so a name typed twice is one client. */
async function resolveClients(plan: ImportPlan): Promise<(name: string) => string | null> {
  const clients = new Map<string, string>();
  for (const client of (await api.clients.list()).clients) clients.set(lower(client.name), client.id);
  for (const at of plan.rows) {
    const name = clampName(plan.sheet.rows[at]?.client);
    if (!name || clients.has(lower(name))) continue;
    const { clients: after } = await api.clients.create(name);
    const made = after.find((client) => lower(client.name) === lower(name));
    if (made) clients.set(lower(name), made.id);
  }
  return (name) => clients.get(lower(clampName(name))) ?? null;
}

/**
 * Runs the plan. `outcomes` is read for what is already done, so calling this
 * again with the same map retries only what failed. Reports each change to
 * `onChange`, which the screen turns into progress.
 */
export async function runImport(
  plan: ImportPlan,
  outcomes: Map<number, RowOutcome>,
  onChange: () => void,
): Promise<void> {
  const todo = plan.rows.filter((at) => outcomes.get(at)?.state !== "done");
  if (todo.length === 0) return;

  const set = (at: number, outcome: RowOutcome) => {
    outcomes.set(at, outcome);
    onChange();
  };

  let names: Awaited<ReturnType<typeof resolveNames>>;
  try {
    names = await resolveNames({ ...plan, rows: todo });
  } catch (problem) {
    for (const at of todo)
      set(at, {
        state: "failed",
        message: `Could not set up the locations and categories: ${reason(problem)}`,
      });
    return;
  }

  let client: (name: string) => string | null = () => null;
  if (plan.shelve && todo.some((at) => clampName(plan.sheet.rows[at]?.client) !== "")) {
    try {
      client = await resolveClients({ ...plan, rows: todo });
    } catch (problem) {
      for (const at of todo)
        set(at, { state: "failed", message: `Could not set up the clients: ${reason(problem)}` });
      return;
    }
  }

  const queue = [...todo];
  const worker = async () => {
    for (let at = queue.shift(); at !== undefined; at = queue.shift()) {
      const row = plan.sheet.rows[at];
      if (!row) continue;
      const before = outcomes.get(at);
      let itemId = before?.state === "failed" ? before.itemId : undefined;
      let shelved = before?.state === "failed" && before.shelved === true;
      set(at, { state: "working" });

      try {
        if (!itemId) {
          const draft = buildDraft(row, plan.sheet, {
            categoryId: names.category(row.category),
            locationId: names.location(row.location),
          });
          itemId = (await api.items.create(draft)).item.id;
        }
      } catch (problem) {
        set(at, { state: "failed", message: reason(problem) });
        continue;
      }

      if (plan.shelve && !shelved) {
        try {
          await api.stock.shelve(itemId, { ...shelveOf(row), receivedOn: plan.receivedOn });
          shelved = true;
        } catch (problem) {
          set(at, {
            state: "failed",
            itemId,
            message: `Saved as an item, but not put on the shelf: ${reason(problem)}`,
          });
          continue;
        }
      }

      const clientId = plan.shelve ? client(row.client) : null;
      if (clientId) {
        const { quantity, unitCostCents } = shelveOf(row);
        const unitPriceCents =
          centsFromInput(row.price) ??
          priceWithMarkup(unitCostCents, normaliseMarkup(plan.markupPercent)) ??
          0;
        try {
          await api.stock.deploy({
            clientId,
            deployedOn: parseDay(row.deployedOn) ?? plan.receivedOn,
            lines: [{ itemId, quantity, unitPriceCents, billable: plan.billable }],
          });
        } catch (problem) {
          set(at, {
            state: "failed",
            itemId,
            shelved: true,
            message: `On the shelf, but not deployed to ${clampName(row.client)}: ${reason(problem)}`,
          });
          continue;
        }
      }
      set(at, { state: "done", itemId });
    }
  };
  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, todo.length) }, worker));
}
